(function (factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    window.RealmSearch = api;
    const initialize = () => api.mount(document, window, window.RealmTheme || {});
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
  }
})(function () {
  function safeUrl(value, origin, product = false) {
    if (typeof value !== 'string' || !value.trim() || /[\u0000-\u0020]/.test(value)) return null;
    try {
      const url = new URL(value, origin);
      if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
      if (product && (url.origin !== origin || !/\/products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname))) return null;
      if (!product && url.origin !== origin && url.hostname !== 'cdn.shopify.com' && !url.hostname.endsWith('.shopify.com') && !url.hostname.endsWith('.shopifyusercontent.com')) return null;
      return url;
    } catch (_) { return null; }
  }
  function previewContext(url, current) {
    for (const key of ['preview_theme_id', 'preview_key']) if (!url.searchParams.has(key) && current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    return url;
  }
  function searchEndpoint({ origin, root = '/', currentUrl = origin, predictiveUrl }) {
    const base = new URL(root, origin);
    if (base.origin !== origin || base.username || base.password) throw new Error('Search root must be same-origin');
    if (!base.pathname.endsWith('/')) base.pathname += '/'; base.search = ''; base.hash = '';
    const endpoint = new URL(predictiveUrl || 'search/suggest', predictiveUrl ? origin : base);
    if (endpoint.origin !== origin || endpoint.username || endpoint.password || !/\/search\/suggest\/?$/.test(endpoint.pathname)) throw new Error('Predictive endpoint must be same-origin');
    endpoint.search = ''; endpoint.hash = '';
    return previewContext(endpoint, new URL(currentUrl, origin));
  }
  function parseSection(markup, query, window, origin, currentUrl) {
    if (typeof markup !== 'string') throw new Error('Predictive search markup unavailable');
    const parsed = new window.DOMParser().parseFromString(markup, 'text/html');
    const root = parsed.querySelector('#RealmPredictiveSearchResult');
    const products = root?.querySelector('[data-realm-search-products]');
    const count = Number(root?.dataset.realmResultCount);
    if (!root || !products || root.dataset.realmQuery?.trim() !== query || !Number.isInteger(count) || count < 0 || count > 10 || products.querySelectorAll('.realm-product-card').length !== count) throw new Error('Predictive search section incomplete');
    root.querySelectorAll('script:not([type="application/json"]), iframe, object, embed, style, link').forEach(node => node.remove());
    const current = new URL(currentUrl, origin);
    for (const node of root.querySelectorAll('*')) {
      for (const attribute of Array.from(node.attributes)) if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name);
      if (node.tagName === 'A') {
        const url = safeUrl(node.getAttribute('href'), origin, !node.hasAttribute('data-realm-search-all'));
        if (!url || node.hasAttribute('data-realm-search-all') && (url.origin !== origin || !/\/search\/?$/.test(url.pathname))) throw new Error('Search result destination invalid');
        node.href = previewContext(url, current).href;
      }
      if (node.tagName === 'IMG') {
        const url = safeUrl(node.getAttribute('src'), origin);
        if (!url) throw new Error('Search image destination invalid');
        node.src = url.href;
      }
      if (node.tagName === 'FORM') {
        const url = new URL(node.getAttribute('action'), origin);
        if (url.origin !== origin || !/\/cart\/add\/?$/.test(url.pathname)) throw new Error('Search product form invalid');
      }
    }
    return { root, count };
  }
  class PredictiveSearchController {
    constructor({ fetch, origin, root = '/', currentUrl = origin, predictiveUrl, sectionId = 'realm-predictive-search', parseResults, setTimeout, clearTimeout, isActive = () => true, onLoading = () => {}, onResults = () => {}, onError = () => {} }) {
      this.endpoint = searchEndpoint({ origin, root, currentUrl, predictiveUrl });
      if (!/^[a-z0-9-]+$/i.test(sectionId) || typeof parseResults !== 'function') throw new Error('Predictive section parser required');
      this.fetch = fetch; this.sectionId = sectionId; this.parseResults = parseResults;
      this.setTimeout = setTimeout; this.clearTimeout = clearTimeout; this.isActive = isActive;
      this.onLoading = onLoading; this.onResults = onResults; this.onError = onError;
      this.generation = 0; this.active = false; this.query = ''; this.timer = null; this.request = null; this.cache = new Map();
    }
    cancel() {
      this.generation++; if (this.timer !== null) this.clearTimeout(this.timer); this.timer = null;
      this.request?.abort(); this.request = null; this.onLoading(false);
    }
    activate(query = this.query, { preserveResults = false } = {}) {
      if (this.active) return;
      this.active = true;
      if (!preserveResults || String(query || '').trim() !== this.query) this.schedule(query);
    }
    deactivate() { this.active = false; this.cancel(); }
    schedule(query) {
      this.query = String(query || '').trim(); this.cancel();
      if (!this.query) { this.onResults({ root: null, count: 0 }, ''); return; }
      if (!this.active || !this.isActive()) return;
      const generation = this.generation;
      this.onLoading(true);
      this.timer = this.setTimeout(() => { this.timer = null; this.run(generation); }, 280);
    }
    async run(generation) {
      if (generation !== this.generation || !this.active || !this.isActive()) return false;
      const query = this.query, url = new URL(this.endpoint);
      url.searchParams.set('q', query); url.searchParams.set('resources[type]', 'product'); url.searchParams.set('resources[limit]', '10'); url.searchParams.set('section_id', this.sectionId);
      const request = new AbortController(); this.request = request; this.onLoading(true);
      let deadline = null, onAbort = null;
      try {
        let markup = this.cache.get(query);
        if (markup === undefined) {
          markup = await new Promise((resolve, reject) => {
            onAbort = () => { const error = new Error('Predictive search cancelled'); error.name = 'AbortError'; reject(error); };
            request.signal.addEventListener('abort', onAbort, { once: true });
            deadline = this.setTimeout(() => {
              const error = new Error('Predictive search request timed out'); error.name = 'TimeoutError';
              reject(error); request.abort();
            }, 8000);
            Promise.resolve().then(() => {
              if (request.signal.aborted) { onAbort(); return; }
              return this.fetch(url.href, { headers: { Accept: 'text/html' }, signal: request.signal });
            }).then(response => {
              if (request.signal.aborted) return;
              if (!response.ok) throw new Error('Predictive search request failed');
              return response.text();
            }).then(resolve, reject);
          });
        }
        if (generation !== this.generation || !this.active || !this.isActive()) return false;
        const results = this.parseResults(markup, query);
        this.cache.set(query, markup);
        if (this.cache.size > 30) this.cache.delete(this.cache.keys().next().value);
        this.onResults(results, query); return true;
      } catch (error) {
        if (error.name !== 'AbortError' && generation === this.generation && this.active && this.isActive()) this.onError(error);
        return false;
      } finally {
        if (deadline !== null) this.clearTimeout(deadline);
        if (onAbort) request.signal.removeEventListener('abort', onAbort);
        if (generation === this.generation) { this.request = null; this.onLoading(false); }
      }
    }
  }
  function mount(document, window, config) {
    const dialog = document.getElementById('RealmSearch'), input = document.getElementById('RealmSearchInput');
    const body = dialog?.querySelector('[data-realm-search-body]'), results = dialog?.querySelector('[data-realm-search-results]');
    const trending = dialog?.querySelector('[data-realm-search-trending]'), empty = dialog?.querySelector('[data-realm-search-empty]');
    const status = dialog?.querySelector('[data-realm-search-status]'), emptyMessage = empty?.querySelector('[data-realm-search-empty-message]'), form = input?.form;
    const feedback = dialog?.querySelector('[data-realm-search-feedback]'), feedbackMessage = feedback?.querySelector('[data-realm-search-feedback-message]'), retry = feedback?.querySelector('[data-realm-search-retry]');
    if (!dialog || !input || !body || !results || !trending || !empty || !status || !form || dialog.dataset.realmSearchBound || typeof window.fetch !== 'function') return;
    dialog.dataset.realmSearchBound = 'true'; input.setAttribute('aria-controls', body.id); input.setAttribute('aria-expanded', 'false'); input.autocomplete = 'off';
    const current = new URL(window.location.href);
    for (const key of ['preview_theme_id', 'preview_key']) if (current.searchParams.has(key) && !form.querySelector('input[name="' + key + '"]')) {
      const context = document.createElement('input'); context.type = 'hidden'; context.name = key; context.value = current.searchParams.get(key); form.append(context);
    }
    for (const link of trending.querySelectorAll('a[href]')) {
      const url = safeUrl(link.getAttribute('href'), window.location.origin, true);
      if (url) link.href = previewContext(url, current).href;
    }
    const sizeImages = scope => {
      scope.querySelectorAll('.realm-card-image img').forEach(image => {
        const width = image.closest('.realm-card-image')?.getBoundingClientRect().width;
        if (!Number.isFinite(width) || width <= 0) return;
        const sizes = Math.ceil(width) + 'px';
        if (image.getAttribute('sizes') !== sizes) image.setAttribute('sizes', sizes);
      });
    };
    const showState = state => {
      dialog.dataset.realmSearchState = state;
      for (const [name, element] of [['trending', trending], ['results', results], ['empty', empty]]) { element.hidden = state !== name; element.inert = state !== name; }
      if (feedback) feedback.hidden = !['loading', 'error'].includes(state);
      input.setAttribute('aria-expanded', String(['trending', 'results'].includes(state)));
      if (state === 'trending') sizeImages(trending);
      if (state === 'results') sizeImages(results);
    };
    const readyModels = scope => scope.querySelectorAll('.realm-card-model').forEach(image => {
      if (image.dataset.realmSearchImageBound) return;
      image.dataset.realmSearchImageBound = 'true';
      const update = () => { image.closest('.realm-product-card').dataset.realmModelReady = String(image.complete && image.naturalWidth > 0); };
      image.addEventListener('load', update); image.addEventListener('error', () => { image.closest('.realm-product-card').dataset.realmModelReady = 'false'; }); update();
    });
    const tabs = Array.from(trending.querySelectorAll('[data-realm-search-tab]'));
    const panels = Array.from(trending.querySelectorAll('[data-realm-search-panel]'));
    const activateTab = tab => {
      for (const item of tabs) { const selected = item === tab; item.setAttribute('aria-selected', String(selected)); item.tabIndex = selected ? 0 : -1; }
      for (const panel of panels) { panel.hidden = panel.dataset.realmSearchPanel !== tab.dataset.realmSearchTab; panel.inert = panel.hidden; }
      sizeImages(trending);
    };
    for (const tab of tabs) {
      tab.addEventListener('click', () => activateTab(tab));
      tab.addEventListener('keydown', event => {
        if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault(); const at = tabs.indexOf(tab);
        const next = event.key === 'Home' ? tabs[0] : event.key === 'End' ? tabs.at(-1) : tabs[(at + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
        activateTab(next); next.focus({ preventScroll: true }); next.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      });
    }
    const isActive = () => dialog.open && !['closed', 'closing'].includes(dialog.dataset.realmDialogState);
    const controller = new PredictiveSearchController({
      fetch: window.fetch.bind(window), origin: window.location.origin, root: config.routes?.root || window.Shopify?.routes?.root || '/', currentUrl: window.location.href,
      predictiveUrl: body.dataset.predictiveUrl, sectionId: body.dataset.predictiveSection,
      parseResults: (markup, query) => parseSection(markup, query, window, window.location.origin, window.location.href),
      setTimeout: window.setTimeout.bind(window), clearTimeout: window.clearTimeout.bind(window), isActive,
      onLoading(active) {
        body.setAttribute('aria-busy', String(active));
        if (active) {
          status.textContent = status.dataset.loadingText;
          if (feedbackMessage) feedbackMessage.textContent = status.dataset.loadingText;
          if (retry) retry.hidden = true;
          showState('loading');
        }
      },
      onError() {
        status.textContent = status.dataset.failedText;
        if (feedbackMessage) feedbackMessage.textContent = status.dataset.failedText;
        if (retry) retry.hidden = false;
        showState('error');
      },
      onResults(response, query) {
        if (!query) { results.replaceChildren(); showState('trending'); status.textContent = ''; return; }
        if (!response.count) {
          results.replaceChildren(); if (emptyMessage) emptyMessage.textContent = emptyMessage.dataset.template.replace('__REALM_QUERY__', query);
          showState('empty'); status.textContent = status.dataset.emptyText; return;
        }
        results.replaceChildren(...Array.from(response.root.childNodes).map(node => document.importNode(node, true)));
        showState('results'); status.textContent = status.dataset.foundText + ' ' + response.count;
        readyModels(results);
        document.dispatchEvent(new window.CustomEvent('realm:search-results-updated', { detail: { scope: results } }));
      }
    });
    input.addEventListener('input', () => controller.schedule(input.value));
    retry?.addEventListener('click', () => {
      input.focus({ preventScroll: true });
      controller.cache.delete(input.value.trim());
      controller.schedule(input.value);
    });
    const visibleProductLinks = () => {
      const state = dialog.dataset.realmSearchState;
      const scope = state === 'results' ? results : state === 'trending' ? panels.find(panel => !panel.hidden) : null;
      return Array.from(scope?.querySelectorAll('.realm-card-image') || []);
    };
    body.addEventListener('focusin', event => {
      const card = event.target.closest?.('.realm-product-card');
      const rail = card?.closest('.realm-search-products,.realm-search-result-products');
      if (!rail || !body.contains(rail)) return;
      const bounds = card.getBoundingClientRect(), viewport = rail.getBoundingClientRect();
      const left = viewport.left + rail.clientLeft, right = left + rail.clientWidth;
      const delta = bounds.left < left ? bounds.left - left : bounds.right > right ? bounds.right - right : 0;
      if (!delta) return;
      const target = Math.max(0, Math.min(rail.scrollWidth - rail.clientWidth, rail.scrollLeft + delta));
      rail.scrollTo({ left: target, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    });
    input.addEventListener('keydown', event => { if (event.key === 'ArrowDown') { const first = visibleProductLinks()[0]; if (first) { event.preventDefault(); first.focus({ preventScroll: true }); } } });
    body.addEventListener('keydown', event => {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const links = visibleProductLinks(), at = links.indexOf(document.activeElement); if (at < 0) return;
      event.preventDefault(); if (event.key === 'ArrowUp' && at === 0) input.focus({ preventScroll: true }); else links[Math.max(0, Math.min(links.length - 1, at + (event.key === 'ArrowDown' ? 1 : -1)))].focus({ preventScroll: true });
    });
    let focusGeneration = 0, focusFrame = null, internalFocusWasChosen = false;
    const cancelInitialFocus = () => {
      focusGeneration++;
      if (focusFrame !== null) window.cancelAnimationFrame?.(focusFrame);
      focusFrame = null;
    };
    const focusWhenEligible = () => {
      cancelInitialFocus();
      const generation = focusGeneration, nativeOpeningFocus = document.activeElement;
      const attempt = () => {
        if (generation !== focusGeneration) return;
        focusFrame = null;
        if (!controller.active || !isActive() || input.isConnected === false) return;
        // Native showModal may initially focus Close. Allow that opening
        // baseline to move to the input; preserve later or deliberately chosen
        // internal focus instead of reclaiming it after the transition.
        const active = document.activeElement;
        if (active === input || active !== dialog && dialog.contains?.(active) && (active !== nativeOpeningFocus || internalFocusWasChosen)) return;
        const style = window.getComputedStyle?.(input);
        const eligible = !input.disabled && !input.hidden && style?.display !== 'none' && !['hidden', 'collapse'].includes(style?.visibility) && (typeof input.getClientRects !== 'function' || input.getClientRects().length > 0);
        if (eligible) {
          input.focus({ preventScroll: true });
          if (document.activeElement === input) return;
        }
        focusFrame = window.requestAnimationFrame(attempt);
      };
      focusFrame = window.requestAnimationFrame(attempt);
    };
    const preserveInternalInteraction = event => {
      if (!event.isTrusted || !isActive()) return;
      internalFocusWasChosen = true; cancelInitialFocus();
    };
    dialog.addEventListener('pointerdown', preserveInternalInteraction, true);
    dialog.addEventListener('keydown', preserveInternalInteraction, true);
    const deactivate = () => { cancelInitialFocus(); if (!dialog.open) internalFocusWasChosen = false; controller.deactivate(); };
    const sync = () => {
      if (isActive()) { if (!controller.active) { controller.activate(input.value); focusWhenEligible(); } }
      else { deactivate(); status.textContent = ''; input.setAttribute('aria-expanded', 'false'); }
    };
    const observer = new window.MutationObserver(sync); observer.observe(dialog, { attributes: true, attributeFilter: ['open', 'data-realm-dialog-state'] });
    dialog.addEventListener('cancel', deactivate); dialog.addEventListener('close', deactivate);
    dialog.addEventListener('click', event => { if (event.target.closest('[data-dialog-close]')) deactivate(); });
    const resizeImages = () => { if (isActive()) sizeImages(dialog); };
    const sizeObserver = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(resizeImages) : null;
    sizeObserver?.observe(dialog); window.addEventListener('resize', resizeImages);
    form.addEventListener('submit', () => { input.value = input.value.trim(); deactivate(); });
    window.addEventListener('pagehide', () => { deactivate(); sizeObserver?.disconnect(); });
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      sizeObserver?.observe(dialog);
      if (isActive()) controller.activate(input.value, { preserveResults: ['trending', 'results', 'empty'].includes(dialog.dataset.realmSearchState) });
      else sync();
      resizeImages();
    });
    readyModels(trending); if (tabs[0]) activateTab(tabs[0]); sync(); return controller;
  }
  return { safeUrl, previewContext, searchEndpoint, parseSection, PredictiveSearchController, mount };
});
